import styled from "styled-components";
import FlexBox, { FlexGrow } from "../../components/FlexBox";
import theme from "../../styles/theme";
import { FilterBar } from "./FilterBar";
import { HistoryRow } from "./HistoryRow";
import axios from "axios";
import React, { useContext, useEffect, useRef, useState } from "react";
import { HistoryType } from "../../types/ChangeHistory";
import LoadingSpinner from "../../components/LoadingSpinner";
import { format, subDays } from "date-fns";
import { VersionContext } from "../../context/VersionContext";
import { useNavigate } from "react-router-dom";

export const ChangeHistory = () => {
  const Header = styled.h4`
    text-align: center;
    font-style: normal;
    font-weight: 700;
    letter-spacing: 0px;
    color: ${theme.color.darkBlueGray900};
    padding: 20px;
    margin-bottom: 0px;
  `;

  const [historyData, setHistoryData] = useState<Array<HistoryType> | null>(
    null
  );
  const [startDate, setStartDate] = useState(subDays(new Date(), 7));
  const [endDate, setEndDate] = useState(new Date());
  const [competency, setCompetency] = useState("");
  const [email, setEmail] = useState("");
  const [isFiltered, setIsFiltered] = useState(false);
  const [objectType, setObjectType] = useState("");
  const { version } = useContext(VersionContext);
  const navigate = useNavigate();
  const filterBarRef = useRef<HTMLDivElement>(null);
  const [filterBarHeight, setFilterBarHeight] = useState(0);

  const formattedStartDate = startDate
    ? format(new Date(startDate), "yyyy-MM-dd")
    : "";
  const formattedEndDate = endDate
    ? format(new Date(endDate), "yyyy-MM-dd")
    : "";

  useEffect(() => {
    if (version.version_number === 0) {
      navigate("/curriculum/version");
    }
    getChangeHistory();
    if (!isFiltered) {
      setEmail("");
      setCompetency("");
    }
  }, [startDate, endDate]);

  useEffect(() => {
    const el = filterBarRef.current;
    if (!el) return;

    const updateHeight = () => setFilterBarHeight(el.offsetHeight);
    updateHeight();

    // const observer = new ResizeObserver(updateHeight);
    // observer.observe(el);
    // return () => observer.disconnect();
  }, [historyData]);

  const getChangeHistory = async () => {
    if (isFiltered) {
      setHistoryData(null);
      await axios
        .get(
          `/curriculum/changehistory?start_date=${
            formattedStartDate || format(new Date(), "yyyy-MM-dd")
          }&end_date=${
            formattedEndDate || format(new Date(), "yyyy-MM-dd")
          }&curriculum_version=${
            version.version_number
          }&competency_code=${competency}&user_email=${email}`
        )
        .then((res) => {
          setHistoryData(res.data.change_history);
        })
        .catch((err) => {
          setHistoryData([]);
          if (err.response.data.message === "missing or malformed jwt") {
            window.location.reload();
          }
        });
    } else {
      setHistoryData(null);
      await axios
        .get(
          `/curriculum/changehistory?start_date=${
            formattedStartDate || format(new Date(), "yyyy-MM-dd")
          }&end_date=${
            formattedEndDate || format(new Date(), "yyyy-MM-dd")
          }&curriculum_version=${version.version_number}`
        )
        .then((res) => {
          setHistoryData(res.data.change_history);
        })
        .catch((err) => {
          setHistoryData([]);
        if (err?.response?.data?.message === "missing or malformed jwt") {
          window.location.pathname="/signin";
          }
        });
    }
  };

  const filterClick = async () => {
    setIsFiltered(true);
    setHistoryData(null);
    await axios
      .get(
        `/curriculum/changehistory?start_date=${
          formattedStartDate || format(new Date(), "yyyy-MM-dd")
        }&end_date=${
          formattedEndDate || format(new Date(), "yyyy-MM-dd")
        }&curriculum_version=${
          version.version_number
        }&competency_code=${competency}&user_email=${email}`
      )
      .then((res) => {
        setHistoryData(res.data.change_history);
      })
      .catch((err) => setHistoryData([]));
  };

  const removeFilter = async () => {
    setIsFiltered(false);
    setCompetency("");
    setEmail("");
    setHistoryData(null);
    await axios
      .get(
        `/curriculum/changehistory?start_date=${
          formattedStartDate || format(new Date(), "yyyy-MM-dd")
        }&end_date=${
          formattedEndDate || format(new Date(), "yyyy-MM-dd")
        }&curriculum_version=${version.version_number}`
      )
      .then((res) => {
        setHistoryData(res.data.change_history);
      })
      .catch((err) => setHistoryData([]));
  };

  if (historyData)
    return (
      <FlexGrow
        style={{
          background: "white",
          overflow: "auto",
          height: "98vh", // Ensures FlexGrow takes full viewport height
          color: "black",
          paddingLeft: "20px",
          display: "flex",
          flexDirection: "column", // Allows vertical stacking of children
        }}
      >
        <Header>Curriculum Change Log</Header>
        {/* <FilterBar /> */}
        <div
          style={{
            // margin: "10px 0px",
            overflow: "auto",
            flex: 1, // Fills remaining space
          }}
          className="visible-scrollbar"
        >
          <div
            ref={filterBarRef}
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 2,
              borderBottom: `1px solid #d8e8fe`,
              marginRight: 20,
            }}
          >
            <FilterBar
              isFiltered={isFiltered}
              filterClick={filterClick}
              setCompetency={setCompetency}
              competency={competency}
              removeFilter={removeFilter}
              startDate={startDate}
              endDate={endDate}
              setStartDate={setStartDate}
              setEndDate={setEndDate}
              email={email}
              setEmail={setEmail}
            />
          </div>

          {historyData.length > 0 ? (
            historyData?.map((data: HistoryType, index: number) => {
              const [date] = data?.change_timestamp.split(" ");
              const [prevDate] =
                historyData[index - 1]?.change_timestamp.split(" ") || [];
              const shouldShowDate = index === 0 || prevDate !== date;
              return (
                <React.Fragment key={data.id}>
                  {shouldShowDate && (
                      <div
                        style={{
                          position: "sticky",
                          top: filterBarHeight,
                          zIndex: 1,
                          background: "white",
                          margin: "20px 0px",
                          fontWeight: "bold",
                          marginRight: 20,
                          paddingBottom: 4,
                          borderBottom: `1px solid #d8e8fe`,
                        }}
                      >
                        {new Date(date).toLocaleDateString("en-US", {
                          month: "short",
                          day: "numeric",
                          year: "numeric",
                        })}
                      </div>
                    )}
                  <HistoryRow data={data} />
                  {/* </div> */}
                </React.Fragment>
              );
            })
          ) : (
            <FlexBox
              style={{ height: "200px", color: theme.color.blue600 }}
              justify="center"
            >
              No History Found!
            </FlexBox>
          )}
        </div>
      </FlexGrow>
    );
  else return <LoadingSpinner />;
};
